/** Every word the public referral form says, in the two languages it offers: English and Hindi.
 *
 *  English is the source and the default, and the English wording of a refusal is **the server's
 *  own sentence, word for word** — the page matches on it to show the Hindi one, so the two must
 *  not drift apart. Refusals that carry a code (`reason_code`) are translated by that code
 *  instead, which is why the gate reads cleanly in Hindi even when the sentence changes.
 */
import { fmtDate } from "@/lib/dates";

export const LANGS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी" },
];

export const COPY = {
  en: {
    title: "Employee referral form",
    subtitle: "Know somebody who would be good at goSTOPS? Put their name in.",
    tabMake: "Make referral",
    tabTrack: "Track referrals",
    helpdesk: "Helpdesk",

    verifyTitle: "First, let us check it is you",
    verifyLead: "Only a goSTOPS employee can send a referral. Find yourself below — type your Employee ID in full, like SHPL1234, or at least three letters of your name.",
    searchPlaceholder: "SHPL1234 or your name",
    codeHint: "Keep going — an Employee ID is four letters and four numbers, like SHPL1234.",
    searching: "Looking…",
    noMatch: "Nobody found. Check the spelling, or type your Employee ID in full.",
    pickYou: "Tap your own name",
    notYou: "Not you?",
    phoneTitle: "Now type your full mobile number",
    phoneLead: "The number on your goSTOPS record ends in {tail}. Type the whole number to verify.",
    phoneHint: "10 digits",
    verifyBtn: "Verify and continue",
    verifying: "Checking…",
    blockedTitle: "This Employee ID cannot refer",
    helpNudge: "Number wrong, or not on your record? Tell the helpdesk",

    referredBy: "Referred by",
    candidateTitle: "Referred candidate details",
    fullName: "Full name",
    mobile: "Mobile number",
    role: "Preferred role",
    pickRole: "What work are they interested in?",
    roleNote: "Our team will match this to an exact role later.",
    experience: "Experience",
    pickExperience: "How long have they worked?",
    branches: "Preferred branches",
    branchesHint: "Tick every branch they are happy to work at",
    allBranches: "All branches",
    ticked: "{n} ticked",
    note: "Anything we should know?",
    optional: "optional",
    notePlaceholder: "e.g. last drawn salary ₹14,000 · currently at a hostel in Jaipur",
    consent: "I have asked this person before putting their name in, and they agree that goSTOPS may call them about work and keep their details for hiring.",
    submit: "Send referral",
    sending: "Sending…",
    thanksTitle: "Referral sent",
    thanksBody: "Our team will call them. You can follow it under Track referrals.",
    another: "Refer somebody else",
    backToSignIn: "Back to sign in",

    trackLead: "Every name you have put in, and where it has reached.",
    trackEmpty: "You have not referred anybody yet.",
    referredOn: "Referred on",
    monthsTitle: "Reward months",
    monthsLead: "A month counts when they are marked present on at least 22 days, for up to 12 months. HR handles the payout — this only shows whether a month counted.",
    monthsCounted: "{n} of 12 months counted",

    helpTitle: "Tell the helpdesk",
    helpLead: "If the number on your record is wrong or missing, leave your Employee ID and a number that does reach you. Somebody from the office will call.",
    helpWho: "Your Employee ID or name",
    helpNumber: "A number we can call you on",
    helpMessage: "What went wrong?",
    helpPlaceholder: "e.g. my number has changed — the one on record is my old one",
    helpSend: "Send to helpdesk",
    helpDone: "Sent. Somebody from the office will call you on that number.",
    close: "Close",

    stages: {
      sent: "Sent", offered: "Offered", successful: "Referral successful",
      closed: "Not taken forward",
    },
    stageHelp: {
      sent: "Our team will call them",
      offered: "They have an offer and a joining date",
      successful: "They joined — the months below are being counted",
      closed: "This one did not go ahead",
    },
    experienceLabels: {
      fresher: "Fresher", "under-1": "Less than 1 year", "1-3": "1 to 3 years",
      "3-5": "3 to 5 years", "5-plus": "More than 5 years",
    },
    cannotRefer: "cannot refer",
    monthStates: {
      qualified: "Counted", not_qualified: "Missed", pending: "Being checked",
      skipped: "Not counted",
    },
    monthReason: {
      checks: "checked on {date}",
      few_days: "fewer than 22 days present",
      referrer_gone: "you were on notice or had left by then",
      person_gone: "they had left by then",
    },
    reasons: {
      left: "This Employee ID is no longer with goSTOPS, so it cannot refer.",
      on_notice: "This Employee ID is on notice, so referrals are closed for it.",
      not_working: "This Employee ID is not marked at work, so it cannot refer.",
      too_new: "A referral can be sent once you have been with goSTOPS for a month. You can refer from {date}.",
      no_number: "We have no mobile number on your record, so we cannot check it is really you. Tell the helpdesk and somebody from the office will call you.",
    },
    errors: {
      already: "This person has already been referred",
      alreadyWithUs: "This person is already with us",
      beingHired: "This person is already being hired with us",
      worksHere: "This person already works at goSTOPS",
      consentNeeded: "Please tick the consent before sending this referral",
      nameNeeded: "The candidate's full name is required",
      candidatePhone: "The candidate's mobile number must be 10 digits",
      myPhone: "Your mobile number must be 10 digits",
      roleNeeded: "Pick the work the candidate is interested in",
      expNeeded: "Pick how much experience they have",
      branchNeeded: "Tick at least one preferred branch",
      notFound: "We could not find that Employee ID — search again",
      noMatchPhone: "That is not the number on your record. Check it, or write to the helpdesk.",
      tooNew: "You can refer from {date}",
      tooNewNoDate: "A referral can be sent once you have been with goSTOPS for a month",
      messageNeeded: "Tell us what went wrong, in a line or two",
      tooMany: "Too many tries from this connection. Try again later.",
      badForm: "We could not read that form",
      tooLong: "That is too much text",
      generic: "Could not send that",
    },
  },

  hi: {
    title: "कर्मचारी रेफ़रल फ़ॉर्म",
    subtitle: "किसी ऐसे को जानते हैं जो goSTOPS में अच्छा काम करेगा? उनका नाम भेजिए।",
    tabMake: "रेफ़रल भेजें",
    tabTrack: "रेफ़रल ट्रैक करें",
    helpdesk: "हेल्पडेस्क",

    verifyTitle: "पहले यह जाँच लें कि यह आप ही हैं",
    verifyLead: "रेफ़रल सिर्फ़ goSTOPS का कर्मचारी भेज सकता है। नीचे अपना नाम ढूँढें — अपना पूरा Employee ID लिखें, जैसे SHPL1234, या नाम के कम से कम 3 अक्षर।",
    searchPlaceholder: "SHPL1234 या आपका नाम",
    codeHint: "लिखते रहिए — Employee ID में चार अक्षर और चार अंक होते हैं, जैसे SHPL1234।",
    searching: "ढूँढ रहे हैं…",
    noMatch: "कोई नहीं मिला। स्पेलिंग जाँचें, या पूरा Employee ID लिखें।",
    pickYou: "अपने नाम पर टैप करें",
    notYou: "आप नहीं हैं?",
    phoneTitle: "अब अपना पूरा मोबाइल नंबर लिखें",
    phoneLead: "हमारे रिकॉर्ड में आपका नंबर {tail} पर ख़त्म होता है। पुष्टि के लिए पूरा नंबर लिखें।",
    phoneHint: "10 अंक",
    verifyBtn: "जाँचें और आगे बढ़ें",
    verifying: "जाँच रहे हैं…",
    blockedTitle: "यह Employee ID रेफ़र नहीं कर सकता",
    helpNudge: "नंबर ग़लत है या रिकॉर्ड में नहीं है? हेल्पडेस्क को बताएँ",

    referredBy: "रेफ़र करने वाले",
    candidateTitle: "रेफ़र किए गए कैंडिडेट की जानकारी",
    fullName: "पूरा नाम",
    mobile: "मोबाइल नंबर",
    role: "पसंदीदा काम",
    pickRole: "वे किस काम में रुचि रखते हैं?",
    roleNote: "सही पद हमारी टीम बाद में तय करेगी।",
    experience: "अनुभव",
    pickExperience: "उन्होंने कितने समय काम किया है?",
    branches: "पसंदीदा ब्रांच",
    branchesHint: "हर उस ब्रांच पर टिक करें जहाँ वे काम करने को तैयार हैं",
    allBranches: "सभी ब्रांच",
    ticked: "{n} चुनी गईं",
    note: "कुछ और बताना चाहेंगे?",
    optional: "वैकल्पिक",
    notePlaceholder: "जैसे पिछली सैलरी ₹14,000 · अभी जयपुर के एक हॉस्टल में",
    consent: "मैंने इनका नाम भेजने से पहले इनसे पूछ लिया है, और ये इस बात से सहमत हैं कि goSTOPS काम के बारे में इन्हें कॉल कर सकता है और हायरिंग के लिए इनकी जानकारी रख सकता है।",
    submit: "रेफ़रल भेजें",
    sending: "भेज रहे हैं…",
    thanksTitle: "रेफ़रल भेज दिया गया",
    thanksBody: "हमारी टीम इन्हें कॉल करेगी। आप इसे ‘रेफ़रल ट्रैक करें’ में देख सकते हैं।",
    another: "किसी और को रेफ़र करें",
    backToSignIn: "साइन इन पर वापस",

    trackLead: "आपने जितने नाम भेजे हैं और वे कहाँ तक पहुँचे हैं।",
    trackEmpty: "आपने अभी तक किसी को रेफ़र नहीं किया है।",
    referredOn: "रेफ़र किया गया",
    monthsTitle: "रिवॉर्ड महीने",
    monthsLead: "जिस महीने में वे कम से कम 22 दिन उपस्थित रहते हैं, वह महीना गिना जाता है — ज़्यादा से ज़्यादा 12 महीने। भुगतान HR देखता है; यहाँ सिर्फ़ यह दिखता है कि महीना गिना गया या नहीं।",
    monthsCounted: "12 में से {n} महीने गिने गए",

    helpTitle: "हेल्पडेस्क को बताएँ",
    helpLead: "अगर रिकॉर्ड में आपका नंबर ग़लत है या है ही नहीं, तो अपना Employee ID और एक ऐसा नंबर लिखें जिस पर आपसे बात हो सके। ऑफ़िस से कोई कॉल करेगा।",
    helpWho: "आपका Employee ID या नाम",
    helpNumber: "जिस नंबर पर कॉल करें",
    helpMessage: "क्या दिक़्क़त है?",
    helpPlaceholder: "जैसे मेरा नंबर बदल गया है — रिकॉर्ड वाला पुराना है",
    helpSend: "हेल्पडेस्क को भेजें",
    helpDone: "भेज दिया गया। ऑफ़िस से कोई उसी नंबर पर कॉल करेगा।",
    close: "बंद करें",

    stages: {
      sent: "भेजा गया", offered: "ऑफ़र हुआ", successful: "रेफ़रल सफल",
      closed: "आगे नहीं बढ़ा",
    },
    stageHelp: {
      sent: "हमारी टीम इन्हें कॉल करेगी",
      offered: "इन्हें ऑफ़र और जॉइनिंग की तारीख़ मिल चुकी है",
      successful: "ये जॉइन कर चुके हैं — नीचे के महीने गिने जा रहे हैं",
      closed: "यह आगे नहीं बढ़ा",
    },
    experienceLabels: {
      fresher: "फ़्रेशर", "under-1": "1 साल से कम", "1-3": "1 से 3 साल",
      "3-5": "3 से 5 साल", "5-plus": "5 साल से ज़्यादा",
    },
    cannotRefer: "रेफ़र नहीं कर सकते",
    monthStates: {
      qualified: "गिना गया", not_qualified: "छूट गया", pending: "जाँच हो रही है",
      skipped: "नहीं गिना गया",
    },
    monthReason: {
      checks: "{date} को जाँचा जाएगा",
      few_days: "22 दिन से कम उपस्थिति",
      referrer_gone: "आप तब तक नोटिस पर थे या जा चुके थे",
      person_gone: "वे तब तक जा चुके थे",
    },
    reasons: {
      left: "यह Employee ID अब goSTOPS के साथ नहीं है, इसलिए रेफ़र नहीं कर सकता।",
      on_notice: "यह Employee ID नोटिस पर है, इसलिए इसके लिए रेफ़रल बंद हैं।",
      not_working: "यह Employee ID काम पर दर्ज नहीं है, इसलिए रेफ़र नहीं कर सकता।",
      too_new: "goSTOPS में एक महीना पूरा होने के बाद ही रेफ़रल भेजा जा सकता है। आप {date} से रेफ़र कर सकते हैं।",
      no_number: "हमारे रिकॉर्ड में आपका मोबाइल नंबर नहीं है, इसलिए हम जाँच नहीं सकते कि यह आप ही हैं। हेल्पडेस्क को बताएँ, ऑफ़िस से कोई कॉल करेगा।",
    },
    errors: {
      already: "यह व्यक्ति पहले ही रेफ़र किया जा चुका है",
      alreadyWithUs: "यह व्यक्ति पहले से हमारे साथ है",
      beingHired: "इस व्यक्ति की हायरिंग पहले से चल रही है",
      worksHere: "यह व्यक्ति पहले से goSTOPS में काम करता है",
      consentNeeded: "रेफ़रल भेजने से पहले सहमति पर टिक करें",
      nameNeeded: "कैंडिडेट का पूरा नाम ज़रूरी है",
      candidatePhone: "कैंडिडेट का मोबाइल नंबर 10 अंकों का होना चाहिए",
      myPhone: "आपका मोबाइल नंबर 10 अंकों का होना चाहिए",
      roleNeeded: "कैंडिडेट किस काम में रुचि रखते हैं, यह चुनें",
      expNeeded: "उनका अनुभव कितना है, यह चुनें",
      branchNeeded: "कम से कम एक पसंदीदा ब्रांच पर टिक करें",
      notFound: "यह Employee ID नहीं मिला — फिर से खोजें",
      noMatchPhone: "यह आपके रिकॉर्ड वाला नंबर नहीं है। इसे जाँचें, या हेल्पडेस्क को लिखें।",
      tooNew: "आप {date} से रेफ़र कर सकते हैं",
      tooNewNoDate: "goSTOPS में एक महीना पूरा होने के बाद ही रेफ़रल भेजा जा सकता है",
      messageNeeded: "एक-दो लाइन में बताइए कि क्या दिक़्क़त है",
      tooMany: "इस कनेक्शन से बहुत बार कोशिश की गई है। थोड़ी देर बाद फिर कोशिश करें।",
      badForm: "हम यह फ़ॉर्म पढ़ नहीं सके",
      tooLong: "यह बहुत ज़्यादा टेक्स्ट है",
      generic: "यह भेजा नहीं जा सका",
    },
  },
};

// The server answers in English. Each rule maps its sentence onto the key above, so the page can
// say it in the language the referrer is reading.
const RULES = [
  [/already been referred/i, "already"],
  [/already being hired/i, "beingHired"],
  [/already works at/i, "worksHere"],
  [/already with us/i, "alreadyWithUs"],
  [/tick the consent/i, "consentNeeded"],
  [/full name is required/i, "nameNeeded"],
  [/candidate's mobile number must be 10 digits/i, "candidatePhone"],
  [/your mobile number must be 10 digits/i, "myPhone"],
  [/pick the work/i, "roleNeeded"],
  [/how much experience/i, "expNeeded"],
  [/preferred branch/i, "branchNeeded"],
  [/could not find that employee id/i, "notFound"],
  [/not the number on your record/i, "noMatchPhone"],
  [/what went wrong/i, "messageNeeded"],
  [/too many tries/i, "tooMany"],
  [/could not read that form/i, "badForm"],
  [/too much text/i, "tooLong"],
];

/** What to show for a refusal: the translated sentence where we recognise it, and the server's
 *  own words where we do not, so nothing is ever swallowed. */
export const serverMessage = (detail, t) => {
  const e = t.errors;
  const text = typeof detail === "string" ? detail
    : Array.isArray(detail) ? String(detail[0]?.msg || "") : "";
  const from = /you can refer from (\d{4}-\d{2}-\d{2})/i.exec(text);
  if (from) return e.tooNew.replace("{date}", fmtDate(from[1]));
  if (/you can refer from/i.test(text)) return e.tooNewNoDate;
  const hit = RULES.find(([re]) => re.test(text));
  return hit ? e[hit[1]] : (text || e.generic);
};

/** The gate's own refusals carry a code, so they read the same in both languages however the
 *  server words them. */
export const reasonText = (info, t) => {
  const key = info?.reason_code;
  if (!key) return null;
  const line = t.reasons[key] || info.reason;
  return line.replace("{date}", info.refer_from ? fmtDate(info.refer_from) : "—");
};
